import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const UsageAnalytics = () => {
  const usageData = [
    { date: '2025-08-27', logins: 145, vaultAccess: 89, sharing: 12 },
    { date: '2025-08-28', logins: 132, vaultAccess: 95, sharing: 8 },
    { date: '2025-08-29', logins: 158, vaultAccess: 102, sharing: 15 },
    { date: '2025-08-30', logins: 167, vaultAccess: 87, sharing: 11 },
    { date: '2025-08-31', logins: 142, vaultAccess: 93, sharing: 9 },
    { date: '2025-09-01', logins: 156, vaultAccess: 108, sharing: 18 },
    { date: '2025-09-02', logins: 171, vaultAccess: 115, sharing: 14 },
    { date: '2025-09-03', logins: 163, vaultAccess: 98, sharing: 16 }
  ];

  const topVaultsData = [
    { name: 'AWS Production', accesses: 245, users: 12, lastAccess: '2 min ago' },
    { name: 'Marketing Tools', accesses: 189, users: 8, lastAccess: '15 min ago' },
    { name: 'Database Credentials', accesses: 156, users: 6, lastAccess: '1 hour ago' },
    { name: 'Social Media', accesses: 134, users: 5, lastAccess: '3 hours ago' },
    { name: 'Development APIs', accesses: 98, users: 9, lastAccess: '5 hours ago' }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const date = new Date(label)?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
      
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-modal">
          <p className="font-medium text-popover-foreground mb-2">{date}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Usage Trends Chart */}
      <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Usage Trends (Last 7 Days)
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={usageData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="loginGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="vaultGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="sharingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
                tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="logins"
                stroke="#1E3A8A"
                fillOpacity={1}
                fill="url(#loginGradient)"
                name="Logins"
              />
              <Area
                type="monotone"
                dataKey="vaultAccess"
                stroke="#0EA5E9"
                fillOpacity={1}
                fill="url(#vaultGradient)"
                name="Vault Access"
              />
              <Area
                type="monotone"
                dataKey="sharing"
                stroke="#059669"
                fillOpacity={1}
                fill="url(#sharingGradient)"
                name="Sharing Events"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center space-x-6 mt-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-sm text-muted-foreground">Logins</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent rounded-full"></div>
            <span className="text-sm text-muted-foreground">Vault Access</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-sm text-muted-foreground">Sharing</span>
          </div>
        </div>
      </div>
      {/* Top Accessed Vaults */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Most Accessed Vaults
        </h3>
        <div className="space-y-4">
          {topVaultsData?.map((vault, index) => (
            <div key={index} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {vault?.name}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {vault?.accesses} accesses
                  </span>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground">
                    {vault?.users} users
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Last: {vault?.lastAccess}
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-primary">
                  #{index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;